/**
 * O fluxo SSE de notificações (C21, ADR-0004).
 *
 * O que viaja no `data:` de cada evento é SÓ isto — o aviso de que um
 * recurso mudou, nunca o dado de domínio. A decisão é de segurança, não de
 * economia (Técnica da C21): se o evento carregasse o dado, a autorização
 * precisaria ser reimplementada no canal, e um erro de roteamento de
 * assinatura vazaria dado de outro tenant. Como o cliente busca pela API,
 * a autorização existe num lugar só.
 */

import { z } from 'zod';

/**
 * Os tipos de recurso assináveis: os três da C21, as notificações da própria
 * pessoa (#536), a sessão dela (21/10/2026) e o funil da organização (26/10).
 */
export const TIPOS_DE_RECURSO_ASSINAVEL = [
  'conversation',
  'inbox',
  'opportunity',
  'notificacao',
  'sessao',
  // O funil da organização (`funil:eu`): uma oportunidade mudou, e o quadro relê.
  'funil',
] as const;
export type TipoDeRecursoAssinavel = (typeof TIPOS_DE_RECURSO_ASSINAVEL)[number];

/**
 * `conversation:{id canônico}` · `inbox:{id externo do inbox}` ·
 * `opportunity:{id canônico}` · `notificacao:eu`. O identificador é o MESMO
 * que a API `/v1` devolve — a notificação só faz sentido se o cliente
 * reconhecer o recurso que ela aponta.
 *
 * `notificacao` só existe como `eu`: a pessoa assina as PRÓPRIAS
 * notificações, e o servidor sabe quem ela é pela sessão. Aceitar um id aqui
 * seria convidar a pergunta "e se eu puser o de outra pessoa?".
 *
 * `sessao:eu` pelo mesmo motivo: avisa que o `/v1/me` de quem assina mudou
 * (uma funcionalidade ligada ou desligada, o papel, o acesso ao prontuário,
 * as preferências da organização), e a tela o relê sem a pessoa recarregar.
 */
export const AssinaturaDeRecursoSchema = z
  .string()
  .regex(
    /^((conversation|inbox|opportunity):[A-Za-z0-9-]{1,64}|notificacao:eu|sessao:eu)$/,
    'Assinatura no formato tipo:id — conversation:{uuid}, inbox:{id}, opportunity:{uuid}, notificacao:eu ou sessao:eu',
  );

export const NotificacaoDeMudancaSchema = z.object({
  /** O recurso que mudou, no mesmo formato da assinatura. */
  recurso: AssinaturaDeRecursoSchema,
});

export type NotificacaoDeMudanca = z.infer<typeof NotificacaoDeMudancaSchema>;
